import { FlexGrid, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import * as wjGrid from '@grapecity/wijmo.grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SaveIcon from '@mui/icons-material/Save';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { configuration } from 'apis';
import { useQuery } from 'react-query';

const PAGE_SIZE = 40;
const EMPTY_ROWS = [];

const numberFields = [
    'eventId',
    'ageCategoryId',
    'divisionId',
    'distanceFrom',
    'distanceTo',
    'weightFrom',
    'weightTo',
    'heightFrom',
    'heightTo'
];

const nullableTextFields = ['additionalDescr', 'distanceDescr', 'weightDescr', 'heightDescr'];

const normalizeNumber = (value) => (value == null || value === '' ? null : Number(value));
const normalizeText = (value) => value?.trim() || null;
const createClientKey = () => `event-category-new-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const unwrapArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.$values)) return value.$values;
    return null;
};

const getEventCategoryRows = (payload) => {
    const directRows = unwrapArray(payload);
    if (directRows) return directRows;

    const rowContainers = [
        payload?.items,
        payload?.Items,
        payload?.eventCategories,
        payload?.EventCategories,
        payload?.rows,
        payload?.Rows,
        payload?.records,
        payload?.Records,
        payload?.result,
        payload?.Result,
        payload?.value,
        payload?.Value
    ];

    for (const container of rowContainers) {
        const rows = unwrapArray(container);
        if (rows) return rows;
    }

    const nestedArray = Object.values(payload ?? {})
        .map(unwrapArray)
        .find((rows) => rows);

    return nestedArray ?? EMPTY_ROWS;
};

const getLookupRows = (payload) => getEventCategoryRows(payload?.data ?? payload?.Data ?? payload);

const normalizeEventCategory = (item) => ({
    ...item,
    id: item.id ?? item.Id ?? null,
    clientKey: item.clientKey ?? item.ClientKey ?? `event-category-${item.id ?? item.Id ?? createClientKey()}`,
    code: item.code ?? item.Code ?? '',
    eventId: item.eventId ?? item.EventId,
    eventName: item.eventName ?? item.EventName ?? '',
    eventAbbr: item.eventAbbr ?? item.EventAbbr ?? '',
    eventTotalAthletes: item.eventTotalAthletes ?? item.EventTotalAthletes ?? null,
    eventIsTeam: item.eventIsTeam ?? item.EventIsTeam ?? false,
    ageCategoryId: item.ageCategoryId ?? item.AgeCategoryId,
    ageCategoryName: item.ageCategoryName ?? item.AgeCategoryName ?? '',
    ageCategoryAbbr: item.ageCategoryAbbr ?? item.AgeCategoryAbbr ?? '',
    ageCategoryGender: item.ageCategoryGender ?? item.AgeCategoryGender ?? '',
    ageCategoryAgeFrom: item.ageCategoryAgeFrom ?? item.AgeCategoryAgeFrom ?? null,
    ageCategoryAgeTo: item.ageCategoryAgeTo ?? item.AgeCategoryAgeTo ?? null,
    ageCategoryIsActive: item.ageCategoryIsActive ?? item.AgeCategoryIsActive ?? false,
    divisionId: item.divisionId ?? item.DivisionId ?? null,
    divisionName: item.divisionName ?? item.DivisionName ?? '',
    divisionAbbr: item.divisionAbbr ?? item.DivisionAbbr ?? '',
    divisionPosition: item.divisionPosition ?? item.DivisionPosition ?? null,
    additionalDescr: item.additionalDescr ?? item.AdditionalDescr ?? '',
    isOlympic: item.isOlympic ?? item.IsOlympic ?? false,
    distanceFrom: item.distanceFrom ?? item.DistanceFrom ?? null,
    distanceTo: item.distanceTo ?? item.DistanceTo ?? null,
    distanceDescr: item.distanceDescr ?? item.DistanceDescr ?? '',
    weightFrom: item.weightFrom ?? item.WeightFrom ?? null,
    weightTo: item.weightTo ?? item.WeightTo ?? null,
    weightDescr: item.weightDescr ?? item.WeightDescr ?? '',
    heightFrom: item.heightFrom ?? item.HeightFrom ?? null,
    heightTo: item.heightTo ?? item.HeightTo ?? null,
    heightDescr: item.heightDescr ?? item.HeightDescr ?? '',
    isNew: item.isNew ?? false,
    isDeleted: item.isDeleted ?? false
});

const normalizeLookup = (item, labelFields = ['description', 'name']) => {
    const id = item.id ?? item.Id;
    const label =
        labelFields.map((field) => item[field] ?? item[field.charAt(0).toUpperCase() + field.slice(1)]).find(Boolean) ??
        item.abbr ??
        item.Abbr ??
        id;

    return {
        ...item,
        id,
        label
    };
};

const buttonSx = {
    minWidth: 120,
    height: 36,
    fontSize: 14,
    textTransform: 'none'
};

const emptyEventCategory = {
    id: null,
    clientKey: '',
    code: '',
    eventId: '',
    ageCategoryId: '',
    divisionId: '',
    additionalDescr: '',
    isOlympic: false,
    distanceFrom: '',
    distanceTo: '',
    distanceDescr: '',
    weightFrom: '',
    weightTo: '',
    weightDescr: '',
    heightFrom: '',
    heightTo: '',
    heightDescr: '',
    isNew: true,
    isDeleted: false
};

const EventCategories = () => {
    const eventCategoriesGridRef = useRef();
    const [page, setPage] = useState(1);
    const [eventCategories, setEventCategories] = useState([]);
    const [eventCategoryChanges, setEventCategoryChanges] = useState({});
    const [originalEventCategory, setOriginalEventCategory] = useState({});
    const [isEventCategoryChanged, setIsEventCategoryChanged] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalItem, setModalItem] = useState(emptyEventCategory);
    const [modalErrors, setModalErrors] = useState({});
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
    const [errorMessage, setErrorMessage] = useState('');

    const dataQuery = useQuery(
        ['eventCategories', page],
        async () => configuration.getEventCategories({ page, pageSize: PAGE_SIZE }),
        { keepPreviousData: true }
    );

    const eventsQuery = useQuery(['eventCategoriesEvents'], async () => configuration.getLKEvents());
    const ageCategoriesQuery = useQuery(['eventCategoriesAgeCategories'], async () => configuration.getAgeCategories());
    const divisionsQuery = useQuery(['eventCategoriesDivisions'], async () => configuration.getDivisions());

    const eventList = useMemo(
        () => getLookupRows(eventsQuery.data).map((item) => normalizeLookup(item, ['name', 'description'])),
        [eventsQuery.data]
    );

    const ageCategoryList = useMemo(
        () => getLookupRows(ageCategoriesQuery.data).map((item) => normalizeLookup(item, ['name', 'description'])),
        [ageCategoriesQuery.data]
    );

    const divisionList = useMemo(
        () => getLookupRows(divisionsQuery.data).map((item) => normalizeLookup(item, ['name', 'description'])),
        [divisionsQuery.data]
    );

    const responsePayload = dataQuery.data?.data ?? dataQuery.data?.Data ?? dataQuery.data;
    const responseRows = getEventCategoryRows(responsePayload);
    const pageData = Array.isArray(responseRows) ? responseRows : EMPTY_ROWS;
    const totalCount = responsePayload?.totalCount ?? responsePayload?.TotalCount ?? pageData.length;
    const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const isGridHydrating = dataQuery.status === 'success' && pageData.length > 0 && eventCategories.length === 0;

    useEffect(() => {
        if (!Array.isArray(pageData)) return;

        const normalized = pageData.map(normalizeEventCategory);
        setEventCategories(normalized);
        setOriginalEventCategory(Object.fromEntries(normalized.filter((item) => item.id != null).map((item) => [item.id, { ...item }])));
        setEventCategoryChanges({});
        setIsEventCategoryChanged(false);
    }, [pageData]);

    const getChecklistErrorMessage = (err, fallback) => {
        const data = err?.response?.data ?? err;

        if (Array.isArray(data?.details) && data.details.length) return data.details.join('\n');

        if (data?.errors && typeof data.errors === 'object') {
            const modelErrors = Object.values(data.errors).flat().filter(Boolean);
            if (modelErrors.length) return modelErrors.join('\n');
        }

        return data?.message || data?.Message || err?.message || fallback;
    };

    const getSelectedGridItem = () => {
        const grid = eventCategoriesGridRef.current;
        if (!grid) return null;

        grid.finishEditing();
        return grid.collectionView?.currentItem ?? null;
    };

    const enrichEventCategory = (item) => {
        const event = eventList.find((eventItem) => Number(eventItem.id) === Number(item.eventId));
        const ageCategory = ageCategoryList.find((ageItem) => Number(ageItem.id) === Number(item.ageCategoryId));
        const division = divisionList.find((divisionItem) => Number(divisionItem.id) === Number(item.divisionId));

        return normalizeEventCategory({
            ...item,
            eventName: event?.name ?? event?.Name ?? '',
            eventAbbr: event?.abbr ?? event?.Abbr ?? '',
            eventTotalAthletes: event?.totalAthletes ?? event?.TotalAthletes ?? null,
            eventIsTeam: event?.isTeam ?? event?.IsTeam ?? false,
            ageCategoryName: ageCategory?.name ?? ageCategory?.Name ?? '',
            ageCategoryAbbr: ageCategory?.abbr ?? ageCategory?.Abbr ?? '',
            ageCategoryGender: ageCategory?.gender ?? ageCategory?.Gender ?? '',
            ageCategoryAgeFrom: ageCategory?.ageFrom ?? ageCategory?.AgeFrom ?? null,
            ageCategoryAgeTo: ageCategory?.ageTo ?? ageCategory?.AgeTo ?? null,
            ageCategoryIsActive: ageCategory?.isActive ?? ageCategory?.IsActive ?? false,
            divisionName: division?.name ?? division?.Name ?? '',
            divisionAbbr: division?.abbr ?? division?.Abbr ?? '',
            divisionPosition: division?.position ?? division?.Position ?? null
        });
    };

    const validateEventCategory = (item) => {
        const errors = {};

        if (!normalizeNumber(item.eventId)) errors.eventId = 'Το πεδίο είναι υποχρεωτικό.';
        if (!normalizeNumber(item.ageCategoryId)) errors.ageCategoryId = 'Το πεδίο είναι υποχρεωτικό.';
        if (item.additionalDescr && item.additionalDescr.length > 100) errors.additionalDescr = 'Μέγιστο 100 χαρακτήρες.';
        if (item.distanceDescr && item.distanceDescr.length > 50) errors.distanceDescr = 'Μέγιστο 50 χαρακτήρες.';
        if (item.weightDescr && item.weightDescr.length > 50) errors.weightDescr = 'Μέγιστο 50 χαρακτήρες.';
        if (item.heightDescr && item.heightDescr.length > 50) errors.heightDescr = 'Μέγιστο 50 χαρακτήρες.';

        numberFields.forEach((field) => {
            if (item[field] != null && item[field] !== '' && Number.isNaN(Number(item[field]))) {
                errors[field] = 'Πρέπει να είναι έγκυρος αριθμός.';
            }
        });

        return errors;
    };

    const buildPayloadItem = (item) => ({
        id: Number(item.id) > 0 ? Number(item.id) : 0,
        code: normalizeText(item.code),
        eventId: normalizeNumber(item.eventId) ?? 0,
        ageCategoryId: normalizeNumber(item.ageCategoryId) ?? 0,
        divisionId: normalizeNumber(item.divisionId),
        additionalDescr: normalizeText(item.additionalDescr),
        isOlympic: item.isOlympic ?? false,
        distanceFrom: normalizeNumber(item.distanceFrom),
        distanceTo: normalizeNumber(item.distanceTo),
        distanceDescr: normalizeText(item.distanceDescr),
        weightFrom: normalizeNumber(item.weightFrom),
        weightTo: normalizeNumber(item.weightTo),
        weightDescr: normalizeText(item.weightDescr),
        heightFrom: normalizeNumber(item.heightFrom),
        heightTo: normalizeNumber(item.heightTo),
        heightDescr: normalizeText(item.heightDescr),
        isNew: item.isNew ?? false,
        isDeleted: item.isDeleted ?? false
    });

    const getValidationMessages = (items) =>
        items
            .filter((item) => !item.isDeleted)
            .flatMap((item, index) =>
                Object.entries(validateEventCategory(item)).map(([field, message]) => `Γραμμή ${index + 1} - ${field}: ${message}`)
            );

    const handleAddEventCategory = () => {
        eventCategoriesGridRef.current?.finishEditing();
        setModalItem({ ...emptyEventCategory });
        setModalErrors({});
        setIsModalOpen(true);
    };

    const handleModalSave = () => {
        const validationErrors = validateEventCategory(modalItem);
        setModalErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) return;

        const enrichedItem = enrichEventCategory({
            ...modalItem,
            clientKey: createClientKey(),
            isNew: true,
            isDeleted: false
        });

        setEventCategories((prev) => [enrichedItem, ...prev]);
        setIsEventCategoryChanged(true);
        setIsModalOpen(false);
        setModalErrors({});
    };

    const handleDeleteEventCategory = () => {
        const selected = getSelectedGridItem();
        if (!selected) {
            setErrorMessage('Παρακαλώ επιλέξτε εγγραφή για διαγραφή.');
            return;
        }

        setDeleteDialog({ open: true, item: selected });
    };

    const handleDeleteConfirm = async () => {
        const selected = deleteDialog.item;
        setDeleteDialog({ open: false, item: null });
        if (!selected) return;

        try {
            if (selected.isNew || !selected.id) {
                const nextEventCategories = eventCategories.filter((item) => item.clientKey !== selected.clientKey);
                const nextChanges = { ...eventCategoryChanges };
                delete nextChanges[selected.clientKey];

                setEventCategories(nextEventCategories);
                setEventCategoryChanges(nextChanges);
                setIsEventCategoryChanged(nextEventCategories.some((item) => item.isNew) || Object.keys(nextChanges).length > 0);
                return;
            }

            const deleteSource = originalEventCategory[selected.id] ?? selected;
            await configuration.postEventCategories([buildPayloadItem({ ...deleteSource, isDeleted: true })]);
            await dataQuery.refetch();
        } catch (err) {
            console.error('Delete failed:', err);
            setErrorMessage(getChecklistErrorMessage(err, 'Η διαγραφή απέτυχε.'));
        }
    };

    const handleEventCategories = async () => {
        try {
            eventCategoriesGridRef.current?.finishEditing();

            const changedItems = eventCategories.filter((item) => item.isNew || item.isDeleted || eventCategoryChanges[item.id]);
            const validationMessages = getValidationMessages(changedItems);

            if (validationMessages.length > 0) {
                setErrorMessage(validationMessages.join('\n'));
                return;
            }

            if (changedItems.length === 0) return;

            const newItemsCount = changedItems.filter((item) => item.isNew).length;
            await configuration.postEventCategories(changedItems.map(buildPayloadItem));

            if (newItemsCount > 0) {
                const lastPageAfterInsert = Math.max(1, Math.ceil((totalCount + newItemsCount) / PAGE_SIZE));
                if (lastPageAfterInsert === page) {
                    await dataQuery.refetch();
                } else {
                    setPage(lastPageAfterInsert);
                }
            } else {
                await dataQuery.refetch();
            }
        } catch (err) {
            console.error('Save failed:', err);
            setErrorMessage(getChecklistErrorMessage(err, 'Η αποθήκευση απέτυχε.'));
        }
    };

    const initializeEventCategory = (flex) => {
        eventCategoriesGridRef.current = flex;
        flex.collectionView.trackChanges = true;

        flex.cellEditEnded.addHandler((s, e) => {
            const editedItem = s.rows[e.row]?.dataItem;
            const editedField = s.columns[e.col]?.binding;
            if (!editedItem || !editedField) return;

            const editedValue = editedField === 'isOlympic' ? Boolean(editedItem[editedField]) : editedItem[editedField];
            const comparableValue = numberFields.includes(editedField) ? normalizeNumber(editedValue) : editedValue;
            const originalValue = originalEventCategory[editedItem.id]?.[editedField];
            const comparableOriginalValue = numberFields.includes(editedField) ? normalizeNumber(originalValue) : originalValue;

            const nextItem = enrichEventCategory({
                ...editedItem,
                [editedField]: comparableValue
            });

            if (editedItem.isNew || comparableValue !== comparableOriginalValue || nullableTextFields.includes(editedField)) {
                setEventCategories((prev) =>
                    prev.map((item) => (item.clientKey === editedItem.clientKey ? { ...nextItem } : item))
                );
                setEventCategoryChanges((prevChanges) => ({
                    ...prevChanges,
                    [editedItem.id ?? editedItem.clientKey]: { ...nextItem }
                }));
                setIsEventCategoryChanged(true);
            }
        });
    };

    const handlePageChange = (event, value) => {
        eventCategoriesGridRef.current?.finishEditing();

        if (isEventCategoryChanged || Object.keys(eventCategoryChanges).length > 0) {
            setErrorMessage('Αποθηκεύστε τις αλλαγές πριν αλλάξετε σελίδα.');
            return;
        }

        setPage(value);
    };

    if (dataQuery.status === 'loading' || isGridHydrating) return <p>Loading...</p>;
    if (dataQuery.status === 'error') return <p>ERROR</p>;

    return (
        <>
            <div style={{ textAlign: 'center', fontSize: 30, fontWeight: 'bold', margin: '20px' }}>
                <h1>Event Categories</h1>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ margin: 0 }}>Event Categories</h2>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        onClick={handleAddEventCategory}
                        startIcon={<AddIcon />}
                        sx={{ ...buttonSx, backgroundColor: '#2196f3', color: '#fff', boxShadow: 2 }}
                    >
                        Προσθήκη
                    </Button>
                    <Button variant="outlined" color="error" onClick={handleDeleteEventCategory} startIcon={<DeleteIcon />} sx={buttonSx}>
                        Διαγραφή
                    </Button>
                    {isEventCategoryChanged && (
                        <Button variant="contained" color="success" onClick={handleEventCategories} startIcon={<SaveIcon />} sx={buttonSx}>
                            Αποθήκευση
                        </Button>
                    )}
                </div>
            </div>
            <FlexGrid
                initialized={(flex) => initializeEventCategory(flex)}
                className="explorer-grid"
                itemsSource={eventCategories.filter((item) => !item.isDeleted)}
                autoSizeMode={1}
                headersVisibility={3}
                alternatingRowStep={0}
            >
                <FlexGridColumn header="Id" binding="id" isReadOnly={true} dataType="Number" />
                <FlexGridColumn header="Code" binding="code" dataType="String" align="left" />
                <FlexGridColumn
                    header="Event"
                    binding="eventId"
                    dataType="Number"
                    dataMap={new wjGrid.DataMap(eventList, 'id', 'label')}
                />
                <FlexGridColumn header="Event Name" binding="eventName" isReadOnly={true} dataType="String" align="left" />
                <FlexGridColumn header="Event Abbr" binding="eventAbbr" isReadOnly={true} dataType="String" align="left" />
                <FlexGridColumn header="Event Total Athletes" binding="eventTotalAthletes" isReadOnly={true} dataType="Number" />
                <FlexGridColumn header="Event Is Team" binding="eventIsTeam" isReadOnly={true} dataType="Boolean" align="center" />
                <FlexGridColumn
                    header="Age Category"
                    binding="ageCategoryId"
                    dataType="Number"
                    dataMap={new wjGrid.DataMap(ageCategoryList, 'id', 'label')}
                />
                <FlexGridColumn header="Age Category Name" binding="ageCategoryName" isReadOnly={true} dataType="String" align="left" />
                <FlexGridColumn header="Age Category Abbr" binding="ageCategoryAbbr" isReadOnly={true} dataType="String" align="left" />
                <FlexGridColumn header="Age Category Gender" binding="ageCategoryGender" isReadOnly={true} dataType="String" align="left" />
                <FlexGridColumn header="Age Category Age From" binding="ageCategoryAgeFrom" isReadOnly={true} dataType="Number" />
                <FlexGridColumn header="Age Category Age To" binding="ageCategoryAgeTo" isReadOnly={true} dataType="Number" />
                <FlexGridColumn header="Age Category Is Active" binding="ageCategoryIsActive" isReadOnly={true} dataType="Boolean" align="center" />
                <FlexGridColumn
                    header="Division"
                    binding="divisionId"
                    dataType="Number"
                    dataMap={new wjGrid.DataMap([{ id: null, label: '' }, ...divisionList], 'id', 'label')}
                    isRequired={false}
                />
                <FlexGridColumn header="Division Name" binding="divisionName" isReadOnly={true} dataType="String" align="left" />
                <FlexGridColumn header="Division Abbr" binding="divisionAbbr" isReadOnly={true} dataType="String" align="left" />
                <FlexGridColumn header="Division Position" binding="divisionPosition" isReadOnly={true} dataType="Number" />
                <FlexGridColumn header="Additional Description" binding="additionalDescr" dataType="String" align="left" />
                <FlexGridColumn header="Is Olympic" binding="isOlympic" dataType="Boolean" align="center" />
                <FlexGridColumn header="Distance From" binding="distanceFrom" dataType="Number" />
                <FlexGridColumn header="Distance To" binding="distanceTo" dataType="Number" />
                <FlexGridColumn header="Distance Description" binding="distanceDescr" dataType="String" align="left" />
                <FlexGridColumn header="Weight From" binding="weightFrom" dataType="Number" />
                <FlexGridColumn header="Weight To" binding="weightTo" dataType="Number" />
                <FlexGridColumn header="Weight Description" binding="weightDescr" dataType="String" align="left" />
                <FlexGridColumn header="Height From" binding="heightFrom" dataType="Number" />
                <FlexGridColumn header="Height To" binding="heightTo" dataType="Number" />
                <FlexGridColumn header="Height Description" binding="heightDescr" dataType="String" align="left" />
            </FlexGrid>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
                <Typography variant="body2">
                    {totalCount} εγγραφές, {PAGE_SIZE} ανά σελίδα
                </Typography>
                <Pagination
                    count={pageCount}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    disabled={isEventCategoryChanged}
                />
            </Stack>

            <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Προσθήκη Event Category</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Code"
                        value={modalItem.code ?? ''}
                        onChange={(e) => setModalItem({ ...modalItem, code: e.target.value })}
                    />
                    <FormControl fullWidth margin="normal" error={!!modalErrors.eventId}>
                        <InputLabel id="event-category-event-label">Event</InputLabel>
                        <Select
                            labelId="event-category-event-label"
                            value={modalItem.eventId || ''}
                            label="Event"
                            onChange={(e) => setModalItem({ ...modalItem, eventId: e.target.value })}
                        >
                            {eventList.map((eventItem) => (
                                <MenuItem key={eventItem.id} value={eventItem.id}>
                                    {eventItem.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {modalErrors.eventId && (
                            <Typography variant="caption" color="error">
                                {modalErrors.eventId}
                            </Typography>
                        )}
                    </FormControl>
                    <FormControl fullWidth margin="normal" error={!!modalErrors.ageCategoryId}>
                        <InputLabel id="event-category-age-label">Age Category</InputLabel>
                        <Select
                            labelId="event-category-age-label"
                            value={modalItem.ageCategoryId || ''}
                            label="Age Category"
                            onChange={(e) => setModalItem({ ...modalItem, ageCategoryId: e.target.value })}
                        >
                            {ageCategoryList.map((ageCategory) => (
                                <MenuItem key={ageCategory.id} value={ageCategory.id}>
                                    {ageCategory.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {modalErrors.ageCategoryId && (
                            <Typography variant="caption" color="error">
                                {modalErrors.ageCategoryId}
                            </Typography>
                        )}
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="event-category-division-label">Division</InputLabel>
                        <Select
                            labelId="event-category-division-label"
                            value={modalItem.divisionId || ''}
                            label="Division"
                            onChange={(e) => setModalItem({ ...modalItem, divisionId: e.target.value })}
                        >
                            <MenuItem value="">-</MenuItem>
                            {divisionList.map((division) => (
                                <MenuItem key={division.id} value={division.id}>
                                    {division.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Additional Description"
                        value={modalItem.additionalDescr ?? ''}
                        error={!!modalErrors.additionalDescr}
                        helperText={modalErrors.additionalDescr}
                        onChange={(e) => setModalItem({ ...modalItem, additionalDescr: e.target.value })}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!modalItem.isOlympic}
                                onChange={(e) => setModalItem({ ...modalItem, isOlympic: e.target.checked })}
                            />
                        }
                        label="Is Olympic"
                    />
                    {[
                        ['distanceFrom', 'Distance From'],
                        ['distanceTo', 'Distance To'],
                        ['distanceDescr', 'Distance Description'],
                        ['weightFrom', 'Weight From'],
                        ['weightTo', 'Weight To'],
                        ['weightDescr', 'Weight Description'],
                        ['heightFrom', 'Height From'],
                        ['heightTo', 'Height To'],
                        ['heightDescr', 'Height Description']
                    ].map(([field, label]) => (
                        <TextField
                            key={field}
                            fullWidth
                            margin="normal"
                            type={numberFields.includes(field) ? 'number' : 'text'}
                            label={label}
                            value={modalItem[field] ?? ''}
                            error={!!modalErrors[field]}
                            helperText={modalErrors[field]}
                            onChange={(e) => setModalItem({ ...modalItem, [field]: e.target.value })}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleModalSave}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
                <DialogTitle>Διαγραφή Event Category</DialogTitle>
                <DialogContent>
                    <DialogContentText>Θέλετε σίγουρα να διαγράψετε την επιλεγμένη εγγραφή;</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, item: null })}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!errorMessage} onClose={() => setErrorMessage('')} maxWidth="sm" fullWidth>
                <DialogContent sx={{ display: 'flex', gap: 3, alignItems: 'center', py: 4 }}>
                    <HighlightOffIcon color="error" sx={{ fontSize: 56, flex: '0 0 auto' }} />
                    <Typography sx={{ whiteSpace: 'pre-line' }}>{errorMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setErrorMessage('')}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EventCategories;
